
import { defineComponent, ref, toRefs, watch } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { Input, Select } from '@/components/input-elements';
import { GET_PRICE_GROUP } from '@/modules/customer/price-groups/graphql/Queries';
import { UPDATE_PRODUCT_PRICE_GROUP } from '@/modules/catalog/products/graphql/Mutations';
import { DELETE_PRODUCT_PRICE_GROUP } from '@/modules/catalog/products/graphql/Mutations';
import { GET_PRODUCT_PRICE_GROUPS } from '@/modules/catalog/products/graphql/Queries';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Table from '../../../../../components/Table/Table.vue';

export default defineComponent({
    props: {
        productId: Number,
        salePrice: {
            type: [Number, String],
            default: null
        }
    },
    components: {
        Input,
        Select,
        Table
    },
    setup(props, context) {
        const i18n = useI18n();
        const store = useStore();
        const { productId, salePrice } = toRefs(props);
        const priceGroups = ref([]) as Record<any, any>;
        const priceGroup = ref(null);
        const discountType = ref('');
        const discountValue = ref<number | string | null>(null);
        const specialPrice = ref<number | string | null>(null);
        const discountError = ref('');
        const groupsList: any = ref([]);
        const priceGroupId = ref();
        const priceGroupBtn = ref(false);
        const clickHandler = ref() as Record<any, any>;
        const currentPage = ref();
        const columnName = ref(null);
        const columnSort = ref(null);
        const pagination = ref({}) as Record<any, any>;
        const page = ref(0);
        const loader = ref(false);
        const discountTypeOptions = ref([
            { label: 'PERCENTAGE', value: 'PERCENTAGE' },
            { label: 'FIXED', value: 'FIXED' }
        ]);
        const columns = ref([
            {
                label: 'message.NAME',
                key: 'name'
            },
            {
                label: 'message.DISCOUNT_TYPE',
                key: 'discount_type'
            },
            {
                label: 'message.DISCOUNT_VALUE',
                key: 'discount_value'
            },
            {
                label: 'message.SPECIAL_PRICE',
                key: 'special_price_original'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        const getSalePrice = () => parseFloat(salePrice.value as any) || 0;

        const calculateSpecialPrice = (type, value) => {
            const sale = getSalePrice();
            const amount = parseFloat(value) || 0;
            if (type === 'PERCENTAGE') {
                return Number((sale - (sale * amount) / 100).toFixed(2));
            }
            if (type === 'FIXED') {
                return Number(amount.toFixed(2));
            }
            return null;
        };

        const getDiscountValueError = (type, value) => {
            const sale = getSalePrice();
            if (value === '' || value === null || isNaN(value)) {
                return i18n.t('message.REQUIRED_FIELD');
            }
            if (type === 'PERCENTAGE') {
                if (sale <= 0) {
                    return 'Please add sale price first';
                }
                if (value < 0 || value > 100) {
                    return 'Value must be between 0 and 100';
                }
            }
            if (type === 'FIXED') {
                if (value < 0 || (sale > 0 && value > sale)) {
                    return sale > 0 ? `Value must be between 0 and ${sale}` : 'Value must be greater than or equal to 0';
                }
            }
            return '';
        };

        const updateFormSpecialPrice = () => {
            if (!discountType.value || discountValue.value === null || discountValue.value === '') {
                specialPrice.value = null;
                return;
            }
            const error = getDiscountValueError(discountType.value, parseFloat(String(discountValue.value)));
            discountError.value = error;
            if (error) {
                specialPrice.value = null;
                return;
            }
            specialPrice.value = calculateSpecialPrice(discountType.value, discountValue.value);
        };

        watch([discountType, discountValue, salePrice], () => {
            if (discountType.value && discountValue.value !== null && discountValue.value !== '') {
                updateFormSpecialPrice();
            }
        });

        const getPriceGroups = () => {
            Apollo.watchQuery({
                query: GET_PRICE_GROUP,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                errorPolicy: 'all'
            }).subscribe(({ data }) => {
                priceGroups.value = [];
                data.price_groups.data.forEach(element => {
                    priceGroups.value.push({
                        label: element.name,
                        value: element.id
                    });
                });
            });
        };

        const getProductPriceGroups = async (pageNum = 0, reload = false, colName = null, type = null) => {
            loader.value = true;
            columnName.value = null;
            columnSort.value = null;
            await Apollo.watchQuery({
                query: GET_PRODUCT_PRICE_GROUPS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    product_id: productId?.value,
                    page: pageNum,
                    limit: 10
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                reload == true ? (currentPage.value = 1) : false;
                clickHandler.value = getProductPriceGroups;
                groupsList.value = (data.product_price_groups.data || []).map(item => ({
                    ...item,
                    discount_type: item.discount_type || '',
                    discount_value: item.discount_value,
                    discount_error: ''
                }));
                pagination.value = data.product_price_groups;
                loader.value = false;
            });
        };

        const resetForm = () => {
            specialPrice.value = null;
            discountType.value = '';
            discountValue.value = null;
            discountError.value = '';
            priceGroup.value = null;
            priceGroupId.value = undefined;
        };

        const handleFormDiscountTypeChange = value => {
            discountType.value = value || '';
            discountError.value = '';
            if (!discountType.value) {
                discountValue.value = null;
                specialPrice.value = null;
            } else {
                updateFormSpecialPrice();
            }
        };

        const addPriceGroup = async () => {
            updateFormSpecialPrice();
            if (!priceGroup.value || !discountType.value || discountValue.value === null || discountValue.value === '' || specialPrice.value == null || discountError.value) {
                Notify.error(i18n.t('message.FILL_ALL_MISSING_FIELDS'));
                return;
            }

            loader.value = true;
            const product_price_group_id = null;
            if (productId?.value) {
                const formData = {
                    id: priceGroupId.value != undefined ? parseInt(groupsList.value[priceGroupId.value].id) : null,
                    product_id: productId.value,
                    price_group_id: priceGroup.value,
                    special_price: specialPrice.value + '',
                    discount_type: discountType.value,
                    discount_value: parseFloat(String(discountValue.value))
                };

                await Apollo.mutate({
                    mutation: UPDATE_PRODUCT_PRICE_GROUP,
                    variables: {
                        input: formData
                    },
                    update: (store, { data: { update_product_price_group } }) => {
                        const pervious_record = store.readQuery({
                            query: GET_PRODUCT_PRICE_GROUPS,
                            variables: {
                                page: page.value,
                                limit: 10,
                                product_id: productId?.value
                            }
                        }) as Record<any, any>;

                        if (priceGroupId.value != undefined) {
                            store.writeQuery({
                                query: GET_PRODUCT_PRICE_GROUPS,
                                variables: {
                                    page: page.value,
                                    limit: 10,
                                    product_id: productId?.value
                                },
                                data: {
                                    product_price_groups: {
                                        ...pervious_record.product_price_groups
                                    },
                                    variables: {
                                        id: update_product_price_group.id
                                    }
                                }
                            });
                        } else {
                            store.writeQuery({
                                query: GET_PRODUCT_PRICE_GROUPS,
                                variables: {
                                    page: 0,
                                    limit: 10,
                                    product_id: productId?.value
                                },
                                data: {
                                    product_price_groups: {
                                        ...pervious_record.product_price_groups,
                                        data: [update_product_price_group, ...pervious_record.product_price_groups.data]
                                    }
                                }
                            });
                        }

                        Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                        loader.value = false;
                        getProductPriceGroups(page.value, false);
                    }
                }).catch(e => {
                    console.log(e);
                    loader.value = false;
                });
            } else {
                const groupName = priceGroups.value.find(ele => ele.value == priceGroup.value);
                if (priceGroupId.value != undefined) {
                    groupsList.value[priceGroupId.value].id = product_price_group_id;
                    groupsList.value[priceGroupId.value].special_price_original = specialPrice.value;
                    groupsList.value[priceGroupId.value].price_group_id = priceGroup.value;
                    groupsList.value[priceGroupId.value].price_group.id = priceGroup.value;
                    groupsList.value[priceGroupId.value].price_group.name = groupName.label;
                    groupsList.value[priceGroupId.value].discount_type = discountType.value;
                    groupsList.value[priceGroupId.value].discount_value = parseFloat(String(discountValue.value));
                } else {
                    groupsList.value.push({
                        id: product_price_group_id,
                        price_group: {
                            id: priceGroup.value,
                            name: groupName.label
                        },
                        price_group_id: priceGroup.value,
                        special_price_original: specialPrice.value,
                        discount_type: discountType.value,
                        discount_value: parseFloat(String(discountValue.value)),
                        discount_error: ''
                    });
                }
                context.emit('priceGroupsData', groupsList.value);
                loader.value = false;
            }

            resetForm();
        };

        const handleCurrentChange = (num: number) => {
            clickHandler.value(num);
        };

        const handleEdit = (index: number) => {
            const item = groupsList.value[index];
            specialPrice.value = item.special_price_original;
            priceGroup.value = item.price_group.id;
            discountType.value = item.discount_type || '';
            discountValue.value = item.discount_value;
            discountError.value = '';
            priceGroupId.value = index;
        };

        const changePriceGroup = (id: number) => {
            const group = groupsList.value.find(ele => ele.price_group_id == id);
            if (group != undefined) {
                if (priceGroupId.value != undefined) {
                    if (groupsList.value[priceGroupId.value].price_group_id == id) {
                        priceGroupBtn.value = false;
                        return;
                    }
                }
                priceGroupBtn.value = true;
                Notify.error(i18n.t('message.PRICE_GROUP_ALREADY_EXISTS'));
            } else {
                priceGroupBtn.value = false;
            }
        };

        const handleRowDiscountTypeChange = (item, value) => {
            item.discount_type = value || '';
            item.discount_error = '';
            if (!item.discount_type) {
                item.discount_value = null;
            }
        };

        const handleRowDiscountValueInput = (item, event) => {
            const rawValue = event.target.value;
            item.discount_value = rawValue === '' ? null : parseFloat(rawValue);
            item.discount_error = '';
        };

        const saveRowPriceGroup = async item => {
            const calculated = calculateSpecialPrice(item.discount_type, item.discount_value);
            item.special_price_original = calculated;

            if (productId?.value && item.id) {
                loader.value = true;
                await Apollo.mutate({
                    mutation: UPDATE_PRODUCT_PRICE_GROUP,
                    variables: {
                        input: {
                            id: parseInt(item.id),
                            product_id: productId.value,
                            price_group_id: item.price_group_id,
                            special_price: calculated + '',
                            discount_type: item.discount_type,
                            discount_value: parseFloat(item.discount_value)
                        }
                    },
                    update: () => {
                        loader.value = false;
                        Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                    }
                }).catch(e => {
                    console.log(e);
                    loader.value = false;
                });
            } else {
                context.emit('priceGroupsData', groupsList.value);
            }
        };

        const handleRowDiscountValueEnter = (item, event) => {
            const rawValue = event.target.value;
            const value = parseFloat(rawValue);
            if (!item.discount_type) {
                return;
            }
            item.discount_value = rawValue === '' ? null : value;
            const error = getDiscountValueError(item.discount_type, rawValue === '' ? NaN : value);
            item.discount_error = error;
            if (error) {
                return;
            }
            saveRowPriceGroup(item);
        };

        const handleDelete = (id: number) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    if (productId?.value) {
                        loader.value = true;
                        await Apollo.mutate({
                            mutation: DELETE_PRODUCT_PRICE_GROUP,
                            variables: { id: parseInt(groupsList.value[id].id) },
                            update: (store, { data: { delete_product_price_group } }) => {
                                loader.value = false;
                                const pervious_record = store.readQuery({
                                    query: GET_PRODUCT_PRICE_GROUPS,
                                    variables: {
                                        page: page.value,
                                        limit: 10,
                                        product_id: productId?.value
                                    }
                                }) as Record<any, any>;

                                const data = pervious_record.product_price_groups.data.filter((t: any) => t.id !== delete_product_price_group);

                                store.writeQuery({
                                    query: GET_PRODUCT_PRICE_GROUPS,
                                    variables: {
                                        page: 0,
                                        limit: 10,
                                        product_id: productId?.value
                                    },
                                    data: {
                                        product_price_groups: {
                                            ...pervious_record.product_price_groups,
                                            data: [...data]
                                        }
                                    }
                                });
                            }
                        });
                        Notify.success('message.RECORD_DELETED_SUCCESSFULLY');
                    } else {
                        groupsList.value.splice(id, 1);
                    }
                    context.emit('priceGroupsData', groupsList.value);
                })
                .catch(e => {
                    console.log(e);
                    loader.value = false;
                });
        };

        return {
            priceGroups,
            priceGroup,
            discountType,
            discountValue,
            discountError,
            discountTypeOptions,
            specialPrice,
            priceGroupId,
            priceGroupBtn,
            groupsList,
            currentPage,
            columns,
            loader,
            salePrice,
            handleEdit,
            handleDelete,
            handleCurrentChange,
            changePriceGroup,
            addPriceGroup,
            getPriceGroups,
            getProductPriceGroups,
            handleFormDiscountTypeChange,
            handleRowDiscountTypeChange,
            handleRowDiscountValueInput,
            handleRowDiscountValueEnter,
            pagination
        };
    }
});
